import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { logout } from "../../redux/actions/authAction";
import { dialog } from "../Common/TreasuryDialog";
import logo from "../../images/Logo.png";

const HEADER_HEIGHT = 68;

/* ── inject mobile CSS once ──────────────────────────────── */
const MOBILE_CSS = `
@media (max-width: 768px) {
  .unifiedheader-desktop-nav { display: none !important; }
  .unifiedheader-hamburger { display: flex !important; }
  .unifiedheader-logo { height: 28px !important; }
  .unifiedheader-auth-group { display: none !important; }
  .unifiedheader-divider { display: none !important; }
}
@media (min-width: 769px) {
  .unifiedheader-desktop-nav { display: flex !important; }
  .unifiedheader-hamburger { display: none !important; }
  .unifiedheader-mobile-drawer { display: none !important; visibility: hidden !important; }
  .unifiedheader-auth-group { display: flex !important; }
}
`;
if (typeof document !== "undefined") {
  let s = document.getElementById("uh-mobile-css");
  if (!s) { s = document.createElement("style"); s.id = "uh-mobile-css"; document.head.appendChild(s); }
  s.textContent = MOBILE_CSS;
}

const NavItem = ({ to, label, active, onClick, mobile }) => {
  const [hovered, setHovered] = useState(false);
  const { i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const style = mobile ? {
    display: "block",
    padding: "13px 20px",
    fontSize: 15,
    fontWeight: 500,
    color: active ? "#c9a84c" : "rgba(255,255,255,0.88)",
    textDecoration: "none",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    background: active ? "rgba(201,168,76,0.10)" : "transparent",
    cursor: "pointer",
    border: "none",
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: "rgba(255,255,255,0.08)",
    width: "100%",
    textAlign: isAr ? "right" : "left",
    fontFamily: "inherit",
  } : {
    display: "inline-block",
    padding: "7px 14px",
    fontSize: 14,
    fontWeight: 500,
    color: active ? "#c9a84c" : hovered ? "#ffffff" : "rgba(255,255,255,0.82)",
    textDecoration: "none",
    borderRadius: 7,
    background: active ? "rgba(201,168,76,0.12)" : hovered ? "rgba(255,255,255,0.08)" : "transparent",
    transition: "background 0.15s, color 0.15s",
    letterSpacing: "0.01em",
    cursor: "pointer",
    border: "none",
    fontFamily: "inherit",
    lineHeight: "1.4",
  };
  if (onClick) {
    return (
      <li style={{ listStyle: "none" }}>
        <button style={style} onClick={onClick}
          onMouseEnter={() => !mobile && setHovered(true)}
          onMouseLeave={() => !mobile && setHovered(false)}>
          {label}
        </button>
      </li>
    );
  }
  return (
    <li style={{ listStyle: "none" }}>
      <Link style={style} to={to}
        onMouseEnter={() => !mobile && setHovered(true)}
        onMouseLeave={() => !mobile && setHovered(false)}>
        {label}
      </Link>
    </li>
  );
};

/* ── Language Switch Button ─────────────────────────────── */
const LanguageSwitcher = ({ mobile }) => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  const toggle = () => {
    i18n.changeLanguage(isAr ? "en" : "ar");
  };

  if (mobile) {
    return (
      <button
        onClick={toggle}
        style={{
          width: "100%", padding: "12px 20px",
          background: "rgba(201,168,76,0.10)",
          border: "none",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          color: "#c9a84c",
          fontSize: 14, fontWeight: 600,
          cursor: "pointer",
          textAlign: isAr ? "right" : "left",
          fontFamily: "inherit",
          display: "flex", alignItems: "center", gap: 8,
          flexDirection: isAr ? "row-reverse" : "row",
          direction: isAr ? "rtl" : "ltr",
        }}
      >
        {/* Globe icon */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        {isAr ? "English" : "العربية"}
      </button>
    );
  }

  return (
    <button className="lang-switch-btn" onClick={toggle} title={isAr ? "Switch to English" : "التبديل إلى العربية"}>
      {/* Globe icon */}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
      {isAr ? "EN" : "ع"}
    </button>
  );
};

/* ── Notification Bell ─────────────────────────────────── */
const NotificationBell = ({ token }) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch("/api/user/notifications", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === 1) setNotifications(data.notifications || []);
    } catch { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = notifications.filter((n) => !n.isRead).length;

  const markAllRead = async () => {
    if (!token || unread === 0) return;
    try {
      await fetch("/api/user/notifications/mark-read", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch { /* silent */ }
  };

  const handleOpen = () => {
    setOpen((v) => !v);
    if (!open) markAllRead();
  };

  const typeIcon = (type) => {
    if (type === "fund_approved") return "✅";
    if (type === "fund_rejected") return "❌";
    if (type === "withdrawal_approved") return "💸";
    if (type === "withdrawal_rejected") return "🚫";
    if (type === "withdrawal_submitted") return "📤";
    return "🔔";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t("notifications.justNow");
    if (mins < 60) return t("notifications.minutesAgo", { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t("notifications.hoursAgo", { count: hrs });
    return t("notifications.daysAgo", { count: Math.floor(hrs / 24) });
  };

  return (
    <div ref={dropRef} style={{ position: "relative" }}>
      <button
        onClick={handleOpen}
        style={{
          position: "relative",
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: "50%",
          width: 38, height: 38,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", flexShrink: 0,
        }}
        title={t("nav.notifications")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -2, right: -2,
            background: "#e53e3e", color: "#fff", borderRadius: "50%",
            width: 17, height: 17, fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            border: "2px solid #0e3725",
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 10px)",
          [isAr ? "left" : "right"]: 0,
          width: 320, maxWidth: "calc(100vw - 32px)",
          background: "#fff", borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          border: "1px solid #e8e6de", zIndex: 2000, overflow: "hidden",
        }}>
          <div style={{
            padding: "14px 18px", borderBottom: "1px solid #e8e6de",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#f9f8f5",
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0e3725" }}>{t("nav.notifications")}</span>
            {unread > 0 && <span style={{ fontSize: 11, color: "#8a8a7e" }}>{unread} {t("nav.unread")}</span>}
          </div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "28px 18px", textAlign: "center", color: "#8a8a7e", fontSize: 13 }}>
                {t("nav.noNotificationsYet")}
              </div>
            ) : (
              notifications.slice(0, 20).map((n) => (
                <div key={n._id} style={{
                  padding: "13px 18px", borderBottom: "1px solid #f0ede6",
                  background: n.isRead ? "#fff" : "#fefce8",
                  display: "flex", gap: 12, alignItems: "flex-start",
                  direction: isAr ? "rtl" : "ltr",
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{typeIcon(n.type)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a14", marginBottom: 2 }}>{n.title}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>{n.message}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{timeAgo(n.createdAt)}</div>
                  </div>
                  {!n.isRead && (
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#c9a84c", flexShrink: 0, marginTop: 5 }} />
                  )}
                </div>
              ))
            )}
          </div>
          {notifications.length > 0 && (
            <div style={{ padding: "10px 18px", borderTop: "1px solid #e8e6de", background: "#f9f8f5", textAlign: "center" }}>
              <Link to="/dashboard/portfolio" onClick={() => setOpen(false)}
                style={{ fontSize: 12, color: "#0e3725", fontWeight: 600, textDecoration: "none" }}>
                {t("nav.viewPortfolio")}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════ */
const UnifiedHeader = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { auth } = useSelector((state) => state);
  const [visible, setVisible] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastScrollY = useRef(0);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < lastScrollY.current || currentY < 80) {
        setVisible(true);
      } else if (currentY > lastScrollY.current && currentY > 120) {
        setVisible(false);
        setMobileOpen(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const user = auth?.data?.user;
  const isLoggedIn = auth?.data?.status === 1;
  const isAdmin = isLoggedIn && user?.role === 1;
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const path = location.pathname;

  const avatarSrc = user?.profile_image && user.profile_image.startsWith("http")
    ? user.profile_image
    : `https://res.cloudinary.com/dfzwynbsl/image/upload/w_40,h_40,c_fill,r_max,co_rgb:f8b602,l_text:Arial_20_bold:${(user?.name || "U").charAt(0).toUpperCase()}/fl_layer_apply/v1773045169/avatar_base.png`;

  const handleLogout = async () => {
    const confirmed = await dialog.confirm(t("dialog.logout"), t("dialog.logoutConfirm"));
    if (confirmed) {
      dispatch(logout());
      history.push("/login");
    }
  };

  const publicLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/howitwork", label: t("nav.howItWorks") },
    { to: "/blog", label: t("nav.news") },
  ];
  const userLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/howitwork", label: t("nav.howItWorks") },
    { to: "/dashboard/portfolio", label: t("nav.portfolio") },
  ];
  const adminLinks = [
    { to: "/admin/dashboard", label: t("nav.dashboard") },
    { to: "/admin/users", label: t("nav.users") },
    { to: "/admin/properties", label: t("nav.properties") },
    { to: "/admin/requestFund", label: t("nav.fundRequests") },
    { to: "/admin/integrations", label: t("nav.integrations") },
  ];
  const links = isAdmin ? adminLinks : isLoggedIn ? userLinks : publicLinks;

  const headerStyle = {
    position: "fixed",
    top: 0, left: 0, right: 0,
    zIndex: 1000,
    height: HEADER_HEIGHT,
    background: "#0e3725",
    boxShadow: "0 2px 18px rgba(0,0,0,0.22)",
    display: "flex",
    alignItems: "center",
    fontFamily: "inherit",
    transition: "transform 0.32s ease, background 0.35s ease, box-shadow 0.35s ease",
    transform: visible ? "translateY(0)" : "translateY(-100%)",
  };

  const ctaLogin = {
    display: "inline-block", padding: "7px 18px", fontSize: 14, fontWeight: 600,
    background: "#c9a84c", color: "#ffffff", textDecoration: "none",
    borderRadius: 6, border: "2px solid #c9a84c", letterSpacing: "0.03em",
    transition: "background 0.18s, transform 0.15s", cursor: "pointer",
  };
  const ctaSignup = {
    display: "inline-block", padding: "7px 18px", fontSize: 14, fontWeight: 600,
    background: "transparent", color: "rgba(255,255,255,0.88)", textDecoration: "none",
    borderRadius: 6, border: "2px solid rgba(255,255,255,0.50)", letterSpacing: "0.03em",
    transition: "background 0.18s, border-color 0.18s", cursor: "pointer",
  };
  const avatarWrap = {
    display: "flex", alignItems: "center", gap: 9,
    padding: "5px 12px 5px 6px", borderRadius: 30,
    background: "rgba(255,255,255,0.07)", cursor: "pointer",
    textDecoration: "none", border: "1px solid rgba(255,255,255,0.12)",
  };
  const avatarImg = {
    width: 32, height: 32, borderRadius: "50%", objectFit: "cover",
    border: "2px solid #c9a84c", flexShrink: 0,
  };
  const avatarName = {
    fontSize: 13.5, fontWeight: 600, color: "#fff",
    maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  };
  const divider = {
    width: 1, height: 22, background: "rgba(255,255,255,0.18)",
    margin: "0 10px", flexShrink: 0,
  };

  return (
    <>
      <header style={headerStyle}>
        {/*
          3-column layout (all direction:ltr, no bidi tricks):
          English: [Logo + NavLinks]  [flex-grow spacer]  [Lang + Auth]
          Arabic:  [Auth + Lang]      [flex-grow spacer]  [NavLinks + Logo]
          The Arabic column order is achieved by rendering the right column first in DOM
          and using flex-direction:row-reverse on the outer container.
        */}
        <div style={{
          width: "100%", maxWidth: 1280, margin: "0 auto",
          padding: "0 20px", display: "flex", alignItems: "center",
          height: "100%", direction: "ltr",
          flexDirection: isAr ? "row-reverse" : "row",
        }}>

          {/* ── RIGHT GROUP (Logo + Nav links) ─────────────────────────── */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
            <Link to={isAdmin ? "/admin/dashboard" : "/"} style={{
              display: "flex", alignItems: "center", gap: 10,
              textDecoration: "none", flexShrink: 0, marginRight: isAr ? 0 : 24, marginLeft: isAr ? 24 : 0,
            }}>
              <img src={logo} alt="Treasury" className="unifiedheader-logo" style={{
                height: 38, width: "auto", filter: "brightness(0) invert(1)",
              }} />
            </Link>
            {/* Nav links — desktop only, adjacent to logo */}
            <nav className="unifiedheader-desktop-nav" style={{
              display: "flex", alignItems: "center", gap: 4,
              flexDirection: "row",
            }}>
              {links.map((link) => (
                <NavItem key={link.to} to={link.to} label={link.label} active={path === link.to} />
              ))}
            </nav>
          </div>

          {/* ── SPACER ─────────────────────────────────────────────────── */}
          <div style={{ flex: 1 }} />

          {/* ── LEFT GROUP (Lang + Auth) ───────────────────────────────── */}
          <div className="unifiedheader-auth-group" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <LanguageSwitcher />
            <div className="unifiedheader-divider" style={divider} />
            {isLoggedIn ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexDirection: isAr ? "row-reverse" : "row" }}>
                {!isAdmin && <NotificationBell token={token} />}
                <Link to={isAdmin ? "/admin/profile" : "/dashboard/profile"} style={avatarWrap}>
                  <img src={avatarSrc} alt={user?.name} style={avatarImg}
                    onError={(e) => { e.target.src = `https://res.cloudinary.com/dfzwynbsl/image/upload/w_40,h_40,c_fill,r_max,co_rgb:f8b602,l_text:Arial_20_bold:U/fl_layer_apply/v1773045169/avatar_base.png`; }} />
                  <span style={avatarName}>{user?.name?.split(" ")[0] || t("nav.viewProfile")}</span>
                </Link>
                <NavItem label={t("nav.logout")} onClick={handleLogout} />
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexDirection: isAr ? "row-reverse" : "row" }}>
                <Link to="/login" style={ctaLogin}>{t("nav.login")}</Link>
                <Link to="/register" style={ctaSignup}>{t("nav.signup")}</Link>
              </div>
            )}
          </div>

          {/* Mobile: lang + notification bell + hamburger — always on trailing edge */}
          <div className="unifiedheader-hamburger" style={{
            display: "none", alignItems: "center", gap: 8,
            flexDirection: isAr ? "row-reverse" : "row",
          }}>
            <LanguageSwitcher />
            {isLoggedIn && !isAdmin && <NotificationBell token={token} />}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              style={{
                background: "rgba(255,255,255,0.07)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 8,
                width: 40, height: 40,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 5, cursor: "pointer", padding: 0,
              }}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <>
                  <span style={{ width: 20, height: 2, background: "rgba(255,255,255,0.9)", borderRadius: 2 }} />
                  <span style={{ width: 20, height: 2, background: "rgba(255,255,255,0.9)", borderRadius: 2 }} />
                  <span style={{ width: 14, height: 2, background: "rgba(255,255,255,0.9)", borderRadius: 2, alignSelf: "flex-start", marginLeft: 3 }} />
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <div className="unifiedheader-mobile-drawer" style={{
        display: "block",
        position: "fixed",
        top: HEADER_HEIGHT,
        left: 0, right: 0,
        background: "#0e3725",
        zIndex: 999,
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        transform: (!visible || !mobileOpen) ? "translateY(-110%)" : "translateY(0)",
        transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
        borderTop: "1px solid rgba(255,255,255,0.10)",
        maxHeight: "calc(100vh - 68px)",
        overflowY: "auto",
        pointerEvents: mobileOpen ? "auto" : "none",
        direction: isAr ? "rtl" : "ltr",
      }}>
        <ul style={{ listStyle: "none", margin: 0, padding: "8px 0" }}>
          {links.map((link) => (
            <NavItem key={link.to} to={link.to} label={link.label} active={path === link.to} mobile />
          ))}
        </ul>

        {/* Language switch in mobile */}
        <LanguageSwitcher mobile />

        {/* Mobile auth section */}
        <div style={{ padding: "12px 16px 20px", borderTop: "1px solid rgba(255,255,255,0.10)" }}>
          {isLoggedIn ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Link
                to={isAdmin ? "/admin/profile" : "/dashboard/profile"}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  flexDirection: isAr ? "row-reverse" : "row",
                  padding: "10px 12px", borderRadius: 10,
                  background: "rgba(255,255,255,0.07)",
                  textDecoration: "none",
                }}
              >
                <img src={avatarSrc} alt={user?.name}
                  style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid #c9a84c", flexShrink: 0 }}
                  onError={(e) => {
                    e.target.src = `https://res.cloudinary.com/dfzwynbsl/image/upload/w_40,h_40,c_fill,r_max,co_rgb:f8b602,l_text:Arial_20_bold:U/fl_layer_apply/v1773045169/avatar_base.png`;
                  }} />
                <div style={{ textAlign: isAr ? "right" : "left" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{user?.name || t("nav.viewProfile")}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>{t("nav.viewProfile")}</div>
                </div>
              </Link>
              <button
                onClick={handleLogout}
                style={{
                  width: "100%", padding: "11px 16px",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: 8, color: "rgba(255,255,255,0.8)",
                  fontSize: 14, fontWeight: 500, cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: isAr ? "right" : "center",
                }}
              >
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Link to="/login" style={{
                display: "block", textAlign: "center", padding: "12px",
                background: "#c9a84c", color: "#fff", borderRadius: 8,
                textDecoration: "none", fontSize: 15, fontWeight: 600,
              }}>
                {t("nav.login")}
              </Link>
              <Link to="/register" style={{
                display: "block", textAlign: "center", padding: "12px",
                background: "transparent", color: "rgba(255,255,255,0.88)",
                border: "1.5px solid rgba(255,255,255,0.35)",
                borderRadius: 8, textDecoration: "none", fontSize: 15, fontWeight: 500,
              }}>
                {t("nav.signup")}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Overlay to close drawer on outside tap */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 998,
            background: "rgba(0,0,0,0.35)",
          }}
        />
      )}

      {/* Spacer so content is not hidden under fixed header */}
      <div style={{ height: HEADER_HEIGHT }} />
    </>
  );
};

export default UnifiedHeader;
